<template>
  <div ref="root" class="sb-tab-menu" :class="computedClasses">
    <template v-if="isLoading">
      <SbLoading type="placeholder" class="sb-tab-menu__loading">
        <SbLoadingPlaceholder
          v-for="placeholder in loadingPlaceholders"
          :key="placeholder.id"
          :width="placeholder.width"
          height="24px"
          margin-bottom="0"
        />
      </SbLoading>
    </template>
    <template v-else>
      <slot name="left-slot" />
      <SbIconButton
        v-if="showTabArrows"
        :is-disabled="!showLeftArrow"
        class="sb-tab-menu__arrow-left"
        :label="previousArrowLabel"
        icon="chevron-left"
        size="medium"
        tabindex="-1"
        @click="() => scrollTabs('left')"
      />
      <nav
        ref="tabWrapper"
        :key="`tab-wrapper-errors-${(props.model?.map((item) => item.has_errors) ?? []).join('-')}`"
        class="sb-tab-menu__wrapper"
      >
        <PrimeTabMenu
          ref="tabMenu"
          v-bind="props"
          :pt="computedPassthrough"
          unstyled
          @tab-change="handleTabChange"
        >
          <template v-if="$slots.item" #item="{ item, props }">
            <slot name="item" :item="item" :props="props" />
          </template>
        </PrimeTabMenu>
      </nav>
      <SbIconButton
        v-if="showTabArrows"
        :is-disabled="!showRightArrow"
        class="sb-tab-menu__arrow-right"
        :label="nextArrowLabel"
        icon="chevron-right"
        size="medium"
        tabindex="-1"
        @click="() => scrollTabs('right')"
      />
      <slot name="right-align-left" />
      <slot name="right-slot" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { SbIconButton, SbLoading, SbLoadingPlaceholder } from '..'
import PrimeTabMenu, { type TabMenuChangeEvent } from 'primevue/tabmenu'
import type { MenuItem } from 'primevue/menuitem'
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import {
  useEventListener,
  useResizeObserver,
  useDebounceFn,
} from '@vueuse/core'
import i18n from '../../i18n/index'
import { generateLoadingPlaceholders } from './helpers'

type TabMenuProps = {
  /**
   * Array of menu items to be displayed in the tab menu.
   */
  model?: MenuItem[]

  /**
   * Index of the currently active tab.
   */
  activeIndex?: number

  /**
   * ID of the element that labels the tab menu.
   */
  ariaLabelledby?: string | undefined

  /**
   * Accessibility label for the tab menu.
   */
  ariaLabel?: string | undefined

  /**
   * Defines the visual style type of the tab menu.
   * Currently supports 'container' style.
   */
  type?: 'container' | undefined

  /**
   * Enables horizontal scrolling when tabs overflow the container width.
   */
  scrollable?: boolean

  /**
   * Shows loading state with placeholder elements.
   */
  isLoading?: boolean

  /**
   * Pass-through configuration object for PrimeVue component styling.
   */
  pt?: object

  /**
   * Locale code for internationalization of arrow button labels.
   */
  locale?: string
}

type TabMenuEmits = {
  'update:activeIndex': [value: number]
  'tab-change': [item: MenuItem]
}

const props = withDefaults(defineProps<TabMenuProps>(), {
  model: () => [],
  activeIndex: 0,
  ariaLabelledby: undefined,
  ariaLabel: undefined,
  type: undefined,
  scrollable: true,
  isLoading: false,
  pt: () => ({}),
  locale: 'en',
})

const emits = defineEmits<TabMenuEmits>()
const root = ref<HTMLElement>()
const tabWrapper = ref<HTMLElement>()
const tabMenu = ref<InstanceType<typeof PrimeTabMenu>>()
const wrapperWidth = ref(0)
const tabMenuWidth = ref(0)
const wrapperScrollPosition = ref(0)
const focusedIndex = ref(-1)

const ACTION_SELECTOR = '.sb-tab-menu__action'

const getRoot = (): HTMLElement | undefined => root.value
const getWrapper = (): HTMLElement | undefined => tabWrapper.value

const computedClasses = computed(() => ({
  'sb-tab-menu--container': props.type === 'container',
  'sb-tab-menu--scrollable': props.scrollable,
}))

const computedPassthrough = computed(() => ({
  root: 'sb-tab-menu__container',
  menu: 'sb-tab-menu__menu',
  menuitem: 'sb-tab-menu__menu-item',
  action: (options: {
    context?: { disabled?: boolean; item?: { disabled?: boolean } }
    item?: { disabled?: boolean }
    disabled?: boolean
  }) => ({
    class: 'sb-tab-menu__action',
    tabindex: '0',
    disabled: false,
    'aria-disabled':
      options.context?.disabled ||
      options.context?.item?.disabled ||
      options.item?.disabled ||
      options.disabled
        ? 'true'
        : undefined,
  }),
  icon: 'sb-tab-menu__icon',
  label: 'sb-tab-menu__label',
  inkbar: 'sb-tab-menu__inkbar',
  hooks: 'sb-tab-menu__hooks',
  ...props.pt,
}))

const nextArrowLabel = computed(() => i18n(props.locale, 'scrollRight'))
const previousArrowLabel = computed(() => i18n(props.locale, 'scrollLeft'))

const loadingPlaceholders = computed(() =>
  generateLoadingPlaceholders(
    root.value?.clientWidth,
    wrapperWidth.value,
    props.scrollable,
  ),
)

const handleTabChange = (e: TabMenuChangeEvent): void => {
  const item = props.model[e.index]
  if (item) {
    emits('tab-change', item)
    emits('update:activeIndex', e.index)
    scrollToItem(e.index)
  }
}

const showTabArrows = computed(
  () => props.scrollable && tabMenuWidth.value > wrapperWidth.value,
)
const showLeftArrow = computed(() => wrapperScrollPosition.value > 0)
const showRightArrow = computed(
  () =>
    Math.ceil(wrapperScrollPosition.value) <
    tabMenuWidth.value - wrapperWidth.value,
)

const SCROLL_DISTANCE = 200

const scrollTabs = (direction: 'left' | 'right'): void => {
  const scrollContainer = getWrapper()
  if (!scrollContainer) {
    return
  }
  const scrollAdd = direction === 'left' ? -SCROLL_DISTANCE : SCROLL_DISTANCE
  scrollContainer.scrollTo({
    top: 0,
    left: scrollContainer.scrollLeft + scrollAdd,
    behavior: 'smooth',
  })
}

const getActions = (): HTMLElement[] =>
  Array.from(
    getWrapper()?.querySelectorAll(ACTION_SELECTOR) || [],
  ) as HTMLElement[]

const getActionIndex = (target: HTMLElement): number =>
  getActions().indexOf(target)

const scrollToItem = (index: number): void => {
  const scrollContainer = getWrapper()
  if (!scrollContainer || index < 0 || index >= props.model.length) {
    return
  }

  const targetElement = getActions()[index]
  if (!targetElement) {
    return
  }

  const containerRect = scrollContainer.getBoundingClientRect()
  const targetRect = targetElement.getBoundingClientRect()
  const containerWidth = containerRect.width
  const targetWidth = targetRect.width
  const paddingOffset = 16

  const isFullyVisible =
    targetRect.left >= containerRect.left + paddingOffset &&
    targetRect.right <= containerRect.right - paddingOffset

  if (isFullyVisible) {
    return
  }

  const availableWidth = containerWidth - paddingOffset * 2
  const isTabWiderThanContainer = targetWidth >= availableWidth

  let scrollOffset = 0

  if (isTabWiderThanContainer) {
    const targetLeftOffset = targetRect.left - containerRect.left
    scrollOffset = targetLeftOffset - paddingOffset
  } else {
    const targetCenter = targetRect.left + targetRect.width / 2
    const containerCenter = containerRect.left + containerRect.width / 2
    scrollOffset = targetCenter - containerCenter
  }

  scrollContainer.scrollTo({
    left: scrollContainer.scrollLeft + scrollOffset,
    behavior: 'smooth',
  })
}

const handleKeydown = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest(ACTION_SELECTOR) || event.key === 'Tab') {
    return
  }

  if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
    event.preventDefault()
    const actions = getActions()
    const currentIndex = getActionIndex(target)
    const newIndex =
      event.key === 'ArrowLeft'
        ? Math.max(0, currentIndex - 1)
        : Math.min(actions.length - 1, currentIndex + 1)

    if (newIndex !== currentIndex && actions[newIndex]) {
      focusedIndex.value = newIndex
      scrollToItem(newIndex)

      setTimeout(() => {
        actions[newIndex]?.focus()
      }, 50)
    }
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    if (target.getAttribute('aria-disabled') === 'true') {
      return
    }

    const currentIndex = getActionIndex(target)
    if (currentIndex >= 0) {
      handleTabChange({ index: currentIndex } as TabMenuChangeEvent)
    }
  }
}

const handleFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest(ACTION_SELECTOR)) {
    return
  }

  const currentIndex = getActionIndex(target)
  if (currentIndex >= 0) {
    focusedIndex.value = currentIndex
    nextTick(() => {
      scrollToItem(currentIndex)
    })
  }
}

const handleScroll = (): void => {
  wrapperScrollPosition.value = getWrapper()?.scrollLeft ?? 0
}

const debouncedHandleScroll = useDebounceFn(handleScroll, 16)

const handleResize = (): void => {
  const wrapper = getWrapper()
  wrapperWidth.value = wrapper?.clientWidth ?? 0
  tabMenuWidth.value = wrapper?.scrollWidth ?? 0
}

const resizeObserverHandleRoot = useResizeObserver(getRoot, handleResize)
const resizeObserverHandleWrapper = useResizeObserver(getWrapper, handleResize)
const cleanUpScroll = useEventListener(
  getWrapper,
  'scroll',
  debouncedHandleScroll,
)

watch(
  () => props.activeIndex,
  (newIndex: number) => {
    nextTick(() => scrollToItem(newIndex))
  },
)

onMounted(() => {
  handleResize()
  handleScroll()
  getWrapper()?.addEventListener('keydown', handleKeydown)
  getWrapper()?.addEventListener('focusin', handleFocus)
  nextTick(() => scrollToItem(props.activeIndex))
})

onBeforeUnmount(() => {
  cleanUpScroll()
  resizeObserverHandleWrapper.stop()
  resizeObserverHandleRoot.stop()
  getWrapper()?.removeEventListener('keydown', handleKeydown)
  getWrapper()?.removeEventListener('focusin', handleFocus)
})
</script>

<style lang="scss" src="./tab-menu.scss" />
